import React, { useCallback, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  BiddingChatState,
  BiddingChatUserSavedQuestionContentTypes,
} from 'store/modules/bidding_chat/types';
import { deleteUserQuestion, fetchUserQuestion } from 'store/modules/bidding_chat/actions';
import { ApplicationState } from 'store';
import createTrack from 'services/actions/tracker_actions';
import SavedQuestionInput from 'components/atoms/saved_question_input';
import SquareLoader from 'components/atoms/square_loader';

interface UserQuestionProps {
  onCloseDrawer: () => void;
  sendMessage: (content: string) => void;
}

const UserQuestion: React.FC<UserQuestionProps> = ({ onCloseDrawer, sendMessage }) => {
  const dispatch = useDispatch();

  const { user_saved } = useSelector<ApplicationState, BiddingChatState>(
    (state) => state.session.bidding_chat,
  );

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isLoadingSaveQuestion, setIsLoadingSaveQuestion] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [listNeedsScroll, setListNeedsScroll] = useState<boolean>(false);

  const questionsList: Array<BiddingChatUserSavedQuestionContentTypes> = Array.isArray(
    user_saved.questions,
  )
    ? user_saved.questions
    : [];

  const contentRef = useRef<HTMLDivElement>(null);
  const listUlRef = useRef<HTMLUListElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const loadingRowRef = useRef<HTMLDivElement>(null);

  const measureListOverflow = useCallback(() => {
    const contentEl = contentRef.current;
    const listEl = listUlRef.current;
    if (!contentEl || !listEl) return;

    const footerH = footerRef.current?.offsetHeight ?? 0;
    const loadingH = loadingRowRef.current?.offsetHeight ?? 0;
    const available = contentEl.clientHeight - footerH - loadingH;

    if (available <= 0) return;

    setListNeedsScroll(listEl.scrollHeight > available + 1);
  }, []);

  useLayoutEffect(() => {
    measureListOverflow();
  }, [measureListOverflow, user_saved.questions, editingId, isLoadingSaveQuestion]);

  useLayoutEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) return undefined;

    const ro = new ResizeObserver(() => measureListOverflow());
    ro.observe(contentEl);

    const listEl = listUlRef.current;
    if (listEl) ro.observe(listEl);

    const footerEl = footerRef.current;
    if (footerEl) ro.observe(footerEl);

    const loadingEl = loadingRowRef.current;
    if (loadingEl) ro.observe(loadingEl);

    return () => ro.disconnect();
  }, [measureListOverflow, user_saved.questions, editingId, isLoadingSaveQuestion]);

  const handleContentClick = (content: string) => {
    createTrack('Pergunte ao Edital - usou pergunta salva');
    sendMessage(content);
    onCloseDrawer();
  };

  const handleDeleteClick = (questionId: number) => {
    createTrack('Pergunte ao Edital - deletou pergunta salva');
    if (editingId === questionId) {
      setEditingId(null);
    }
    dispatch(deleteUserQuestion(questionId));
    setDeletingId(questionId);
  };

  const handleEditClick = (questionId: number) => {
    setEditingId(questionId);
  };

  useEffect(() => {
    dispatch(fetchUserQuestion());
  }, [dispatch]);

  useEffect(() => {
    setIsLoadingSaveQuestion(false);
  }, [user_saved.questions]);

  useEffect(() => {
    if (editingId !== null) {
      const list = Array.isArray(user_saved.questions) ? user_saved.questions : [];
      if (!list.some((q) => q.id === editingId)) {
        setEditingId(null);
      }
    }
  }, [user_saved.questions, editingId]);

  return (
    <div
      ref={contentRef}
      className={`bidding-chat-saved-questions-content${
        listNeedsScroll ? ' bidding-chat-saved-questions-content--list-overflow' : ''
      }`}
    >
      <div className="bidding-chat-saved-questions-list-outer">
        <ul ref={listUlRef} className="mb-0 list-unstyled bidding-chat-saved-questions-list">
          {questionsList.map((question) => (
            <li key={question.id}>
              {editingId === question.id ? (
                <div className="bidding-chat-saved-question-line d-flex py-1 align-items-center w-100">
                  <SavedQuestionInput
                    question={question}
                    setEditingId={setEditingId}
                    setIsLoadingSaveQuestion={setIsLoadingSaveQuestion}
                  />
                </div>
              ) : (
                <div className="bidding-chat-saved-question-line d-flex py-1 align-items-center w-100">
                  <button
                    type="button"
                    className="btn btn-link text-left flex-grow-1 min-w-0"
                    onClick={() => handleContentClick(question.content)}
                  >
                    {question.content}
                  </button>
                  <div className="d-flex flex-shrink-0 align-items-center bidding-chat-saved-question-line-actions">
                    <button
                      type="button"
                      className="databalloon btn btn-link pb-0 flex-shrink-0 bidding-chat-saved-question-action-btn"
                      aria-label="Clique para editar a pergunta"
                      data-balloon-pos="left"
                      onClick={() => handleEditClick(question.id)}
                    >
                      <i className="material-icons-outlined" aria-hidden>
                        edit_note
                      </i>
                    </button>
                    <button
                      type="button"
                      className="databalloon btn btn-link pb-0 flex-shrink-0 bidding-chat-saved-question-action-btn"
                      aria-label="Clique para deletar a pergunta"
                      data-balloon-pos="left"
                      onClick={() => handleDeleteClick(question.id)}
                    >
                      {deletingId === question.id ? (
                        <SquareLoader customClass="delete-question-loading" />
                      ) : (
                        <i className="material-icons-outlined" aria-hidden>
                          delete_outline
                        </i>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
      {isLoadingSaveQuestion && (
        <div
          ref={loadingRowRef}
          className="bidding-chat-saved-question-line bidding-chat-saved-questions-loading-row d-flex align-items-center justify-content-center p-2"
        >
          <div className="p-1 position-relative">
            Carregando <SquareLoader customClass="save-question-loading" />
          </div>
        </div>
      )}
      <div ref={footerRef} className="bidding-chat-saved-questions-footer">
        {editingId === null && (
          <SavedQuestionInput
            question={null}
            setEditingId={setEditingId}
            setIsLoadingSaveQuestion={setIsLoadingSaveQuestion}
          />
        )}
      </div>
    </div>
  );
};

export default UserQuestion;
